// Reads a PDF in the browser with pdf.js and gives back each page's text, positioned and with
// its fonts, for pdf_structure to turn into sections.
import * as pdfjs from "pdfjs-dist";
import worker_url from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import { PdfPageText, PdfTextItem } from "./pdf_structure";

pdfjs.GlobalWorkerOptions.workerSrc = worker_url;

export async function extractPdfText(
  data: ArrayBuffer,
  onProgress?: (done: number, total: number) => void
): Promise<PdfPageText[]> {
  const doc = await pdfjs.getDocument({ data: new Uint8Array(data), verbosity: 0 }).promise;
  try {
    const pages: PdfPageText[] = [];
    for (let number = 1; number <= doc.numPages; number++) {
      const page = await doc.getPage(number);
      // The text only names fonts by an internal id. Getting the operator list loads
      // the fonts themselves, which have the real names (and so whether they're bold or italic).
      await page.getOperatorList();
      const text = await page.getTextContent();
      const viewport = page.getViewport({ scale: 1 });
      const items: PdfTextItem[] = [];
      for (const item of text.items) {
        if (!("str" in item) || item.str === "") continue;
        let font = item.fontName;
        try {
          font = page.commonObjs.get(item.fontName)?.name ?? item.fontName;
        } catch {
          // Not loaded, so the internal id will have to do.
        }
        items.push({
          str: item.str,
          font: font.replace(/^[A-Z]{6}\+/, ""), // Drop the subset prefix, like "CFGFPT+"
          size: Math.round(Math.hypot(item.transform[2], item.transform[3]) * 10) / 10,
          x: item.transform[4],
          y: item.transform[5],
          width: item.width,
        });
      }
      pages.push({ number, width: viewport.width, height: viewport.height, items });
      page.cleanup();
      onProgress?.(number, doc.numPages);
    }
    return pages;
  } finally {
    await doc.destroy();
  }
}
